<template>
  <form
    ref="multiStoreDrawerForm"
    class="multi-store-drawer"
    :action="action"
    method="post"
    @submit.prevent="handleLocalizationFormSubmit"
  >
    <input
      name="_method"
      type="hidden"
      value="put"
    >

    <input
      name="return_to"
      type="hidden"
      :value="returnTo"
    >

    <div class="multi-store-drawer__grid">
      <div
        v-if="isMobile"
        class="multi-store-drawer__language-container"
      >
        <div class="multi-store-drawer__filter multi-store-drawer__filter--language">
          <div class="multi-store-drawer__languages">
            <radio-input
              v-for="language of languages"
              :id="`multi-store-drawer-language-${language.code}`"
              :key="language.code"
              v-model="currentLanguageCode"
              :label="language.name"
              name="language_code"
              :value="language.code"
            />
          </div>
        </div>
      </div>

      <div
        class="multi-store-drawer__location-container"
        role="region"
        :aria-label="$string('general.multi_store.drawer.country_filter')"
      >
        <div class="multi-store-drawer__heading-row">
          <div class="multi-store-drawer__heading-item form-group">
            <label
              class="visually-hidden"
              for="multi-store-drawer-search"
              v-text="$string('general.multi_store.drawer.search_description')"
            />

            <input
              id="multi-store-drawer-search"
              v-model="searchString"
              :placeholder="$string('general.multi_store.drawer.search_placeholder')"
              type="search"
              @keydown="handleSearchKeydown"
            >

            <div
              class="visually-hidden"
              aria-live="assertive"
            >
              <span v-text="countryResultLiveCount" />
            </div>
          </div>
        </div>

        <div class="multi-store-drawer__filter multi-store-drawer__filter--location">
          <div class="multi-store-drawer__filter-column">
            <ul>
              <li class="visually-hidden">
                <span v-text="$string('general.multi_store.drawer.continent_button_warning')" />
              </li>
              <li
                v-for="(continent, index) of continentsFilter"
                :key="index"
                class="multi-store-drawer__continent"
              >
                <button
                  class="button button--text multi-store-drawer__continent-button"
                  :class="{ [$class('active')]: targetContinent === continent }"
                  :aria-current="targetContinent === continent"
                  type="button"
                  @click="handleContinentFilter(continent)"
                  v-text="continent"
                />
              </li>
            </ul>
          </div>

          <fieldset class="multi-store-drawer__filter-column">
            <legend
              class="visually-hidden"
              v-text="$string('general.multi_store.options.country')"
            />

            <radio-input
              v-for="(country, index) of countries"
              :id="`multi-store-drawer-country-${country.code}`"
              :ref="`country-${country.code}`"
              :key="country.code"
              v-model="currentCountryCode"
              :label="country.name"
              name="country_code"
              :value="country.code"
              :tabindex="index > 0 ? -1 : 0"
            />
          </fieldset>
        </div>
      </div>

      <div
        v-if="!isMobile"
        class="multi-store-drawer__language-container"
      >
        <div class="multi-store-drawer__heading-row">
          <h2
            id="multi-store-drawer-language-fieldset-title"
            class="multi-store-drawer__heading-item text-body-m-regular"
            v-text="$string('general.multi_store.drawer.language_heading')"
          />
        </div>

        <div class="multi-store-drawer__filter multi-store-drawer__filter--language">
          <fieldset
            class="multi-store-drawer__languages"
            aria-labelledby="multi-store-drawer-language-fieldset-title"
          >
            <radio-input
              v-for="(language, index) of languages"
              :id="`multi-store-drawer-language-${language.code}`"
              :key="language.code"
              v-model="currentLanguageCode"
              :label="language.name"
              name="language_code"
              :value="language.code"
              :tabindex="index > 0 ? -1 : 0"
            />
          </fieldset>

          <button
            v-if="!isMobile"
            class="
              button
              button--block
              button--center
              button--secondary
              multi-store-drawer__confirm
            "
            type="submit"
            @submit.prevent="handleLocalizationFormSubmit"
            v-text="$string('general.multi_store.drawer.confirm')"
          />
        </div>
      </div>

      <button
        v-if="isMobile"
        class="
          button
          button--block
          button--center
          button--secondary
          multi-store-drawer__confirm
        "
        type="submit"
        @submit.prevent="handleLocalizationFormSubmit"
        v-text="$string('general.multi_store.drawer.confirm')"
      />
    </div>
  </form>
</template>

<script>

/**
 * Vue: Multi store drawer (multi-store-drawer)
 * -----------------------------------------------------------------------------
 * Drawer to control multi-store settings.
 *
 * @param {Object} overlay - Overlay API.
 *
 */
import { mapActions, mapState } from 'vuex'
import { multiStoreAction, multiStoreReturnTo } from '~/helpers/multi-store'

import RadioInput from '~async/utils/radio-input/radio-input'

export default {
  name: 'MultiStoreDrawer',

  components: {
    RadioInput,
  },

  props: {
    overlay: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      currentCountryCode: '',
      currentCountryCodeInput: null,
      currentLanguageCode: '',
      filteredCodes: [],
      searchString: '',
      targetContinent: '',
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      allLanguages: (state) => state['multi-store'].allLanguages,
      continents: (state) => state['multi-store'].continents,
      current: (state) => state['multi-store'].current,
      isMobile: (state) => state.index.screen.mobile,
      selectedStore: (state) => state['multi-store'].selectedStore,
      suggestedStore: (state) => state['multi-store'].suggestedStore,
      supported: (state) => state['multi-store'].supported,
    }),

    /**
     * Compute active value.
     * - This is selected if user has selected a country from the list of
     *   available countries.
     */
    active() {
      return this.selectedStore.country ? this.selectedStore : this.suggestedStore
    },

    /**
     * Compute form action based on current country code.
     * @returns {String}
     */
    action() {
      return multiStoreAction(this.active?.instance)
    },

    /**
     * Compute continents array.
     * @returns {Array}
     */
    continentsFilter() {
      return Object.keys(this.continents)
    },

    /**
     * Compute countries array.
     * @returns {Array}
     */
    countries() {
      if (!Object.keys(this.supported).length) {
        return [this.current.country]
      }

      const filteredByContinent = this.filteredCodes.length
        ? this.getFilteredByContinentCountryCodes()
        : this.supported

      const filteredBySearch = this.searchString.length
        ? this.getFilteredBySearch(filteredByContinent)
        : filteredByContinent

      const filteredAndSupported = filteredBySearch

      return Object.values(filteredAndSupported)
        .map((value) => value.country)
        .sort((a, b) => {
          return a.name?.localeCompare(
            b.name,
            this.current.language.code.toLowerCase(),
          )
        })
    },

    /**
     * Compute string for live country result count for screen reader users.
     * @returns {String}
     */
    countryResultLiveCount() {
      return this.$string('general.multi_store.drawer.country_result_count', {
        replace: {
          count: this.countries.length,
        },
        pluralise: this.countries.length,
      })
    },

    /**
     * Compute language choices based on current country.
     * @returns {Array}
     */
    languages() {
      if (!this.currentCountryCode) {
        return this.allLanguages
      }

      if (!this.supported[this.currentCountryCode]?.languages?.length) {
        return [this.current.language]
      }

      const sortedLanguages = this.supported[this.currentCountryCode]?.languages

      sortedLanguages.sort((a, b) => {
        return a.name.localeCompare(
          b.name,
          this.current.language.code.toLowerCase(),
        )
      })

      return sortedLanguages
    },

    /**
     * Compute return to URL.
     * @returns {String}
     */
    returnTo() {
      return multiStoreReturnTo(this.active)
    },
  },

  watch: {

    /**
     * Watch current country code to update selected store.
     * - Also tests to see if previously selected language exists in country.
     * @param {String} countryCode - New country code.
     */
    currentCountryCode(countryCode) {
      if (!countryCode) {
        return
      }

      const matchedLanguage = this.languages.find((language) => {
        return language.code?.toUpperCase() === this.currentLanguageCode?.toUpperCase()
      })

      if (!matchedLanguage) {
        this.currentLanguageCode = this.languages[0].code?.toUpperCase()
      }

      if (this.supported[countryCode]) {
        this.setSelectedStore({
          country: this.supported[countryCode].country,
          instance: this.supported[countryCode].instance,
          language: matchedLanguage || this.languages[0],
        })
      }
    },

    /**
     * Watch current language code to update selected store.
     * @param {String} languageCode - New language code.
     */
    currentLanguageCode(languageCode) {
      const matchedLanguage = this.languages.find((language) => {
        return language.code?.toUpperCase() === languageCode?.toUpperCase()
      })

      if (this.supported[this.currentCountryCode]) {
        this.setSelectedStore({
          country: this.supported[this.currentCountryCode].country,
          instance: this.supported[this.currentCountryCode].instance,
          language: matchedLanguage || this.languages[0],
        })
      }
    },

    /**
     * Watch search string to clear filters when user begins typing.
     */
    searchString() {
      if (this.searchString.length > 0) {
        this.filteredCodes = []
      }
    },

    /*
    * Watch countries to update focus trap.
    */
    countries() {
      this.updateFocusTrap()
    },

    /**
     * Watch languages to update focus trap.
     */
    languages() {
      this.setCurrentCountryCodeInput()
      this.updateFocusTrap(true)
    },

  },

  mounted() {
    this.overlay.addCallback(this.resetState)
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      setPreference: 'multi-store/setPreference',
      setSelectedStore: 'multi-store/setSelectedStore',
    }),

    /**
     * Filter the object of supported country codes by continent.
     * @returns {Object}
     */
    getFilteredByContinentCountryCodes() {
      return Object.keys(this.supported)
        .filter((countryCode) => this.filteredCodes.includes(countryCode))
        .reduce((filteredObject, countryCode) => {
          filteredObject[countryCode] = this.supported[countryCode]
          return filteredObject
        }, {})
    },

    /**
     * Filter available countries by user search string.
     * @param {Object} filteredCountries - Visible countries.
     */
    getFilteredBySearch(filteredCountries) {
      const searchedCountries = Object.entries(filteredCountries)
        .filter(([, supportedCountry]) => {
          return supportedCountry.country.name.toLowerCase().includes(
            this.searchString.toLocaleLowerCase(),
          )
        })

      return Object.fromEntries(searchedCountries)
    },

    /**
     * Filter list of available countries based on selected continent.
     * @param {String} targetContinent - Continent to filter by.
     */
    handleContinentFilter(targetContinent) {
      this.targetContinent = targetContinent
      this.filteredCodes = this.continents[targetContinent]
    },

    /**
     * Handle localization form submit.
     */
    async handleLocalizationFormSubmit() {
      const form = this.$refs.multiStoreDrawerForm
      const formData = new FormData(form)

      this.setPreference({
        country: formData.get('country_code'),
        language: formData.get('language_code'),
      })

      /**
       * Wait for preferences to be set and action/returnTo computed props to
       * finish calculating.
       */
      await this.$nextTick()
      form.submit()
    },

    /**
     * Prevents form from submitting if a user presses Enter on the search
     * input.
     * @param {Event} event - Keydown event.
     */
    handleSearchKeydown(event) {
      if (event.key === 'Enter') {
        event.preventDefault()
      }

      return event.key
    },

    /**
     * Reset form options to their default.
     */
    resetState() {
      this.currentCountryCode = ''
      this.currentLanguageCode = ''
      this.filteredCodes = []
      this.searchString = ''
      this.targetContinent = ''
    },

    /**
     * Update drawer focus trap.
     */
    updateFocusTrap(focusOnCountry = false) {
      if (!this.overlay.isActive) {
        return
      }
      this.overlay.update({ currentElement: document.activeElement })

      if (focusOnCountry) {
        setTimeout(() => {
          this.currentCountryCodeInput.focus({ preventScroll: true })
        }, 350)
      }
    },

    /**
     * Set global current country code input to account for when users use
     * keyboard arrows to navigate through country list.
     */
    setCurrentCountryCodeInput() {
      const currentCountryCodeEl = this.$refs[`country-${this.currentCountryCode}`][0].$el
      const currentCountryCodeInput = currentCountryCodeEl.getElementsByTagName('input')[0]

      this.currentCountryCodeInput = currentCountryCodeInput
    },
  },
}
</script>

<style lang="scss">
@import './multi-store-drawer';
</style>
